import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

let onSessionExpired: (() => void) | null = null;

export function setSessionExpiredHandler(fn: () => void) {
  onSessionExpired = fn;
}

// Concurrent 401s share one refresh request; the API rotates the refresh token on every call.
let refreshInFlight: Promise<boolean> | null = null;

function refreshSession(client: AxiosInstance): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = requestNewSession(client).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function requestNewSession(client: AxiosInstance): Promise<boolean> {
  try {
    await client.post('/auth/refresh', undefined, { params: { audience: 'business' } });
    return true;
  } catch {
    return false;
  }
}

type ExpiredSessionError = AxiosError & { config: InternalAxiosRequestConfig };

// Account changes that run on the current session, so an expired access token is refreshed.
const SIGNED_IN_AUTH_PATHS = [
  '/auth/password/change',
  '/auth/email/change',
  '/auth/account/delete',
  '/auth/mfa/status',
  '/auth/mfa/authenticator-app/setup',
  '/auth/mfa/authenticator-app/confirm',
  '/auth/mfa/verify',
  '/auth/mfa/recovery-codes',
  '/auth/mfa/disable',
];

// Any other 401 from /auth/* means bad credentials, not an expired session, so it is not refreshed.
function isSessionExpired(error: unknown): error is ExpiredSessionError {
  if (!axios.isAxiosError(error) || error.response?.status !== 401 || !error.config) return false;
  const { url = '', retriedAfterRefresh } = error.config;
  return !retriedAfterRefresh && (!url.startsWith('/auth/') || SIGNED_IN_AUTH_PATHS.includes(url));
}

// On an expired session: refresh once, then replay the original request. If the refresh fails
// the user is signed out through the session-expired handler.
export async function refreshOnUnauthorized(client: AxiosInstance, error: unknown) {
  if (!isSessionExpired(error)) throw error;

  const refreshed = await refreshSession(client);
  if (refreshed) return client.request({ ...error.config, retriedAfterRefresh: true });

  onSessionExpired?.();
  throw error;
}
